import z from 'zod'
import _ from 'lodash'
import { cidForDirItemId, cidForDirValue } from '../concept/cid'

// strictly reject empty strings, expect nulls instead
// for simpler comparison
const notEmptyString = z.string().min(1)

export const ZDirectoryItem = z.object({
	ItemID: z.number(),
	Class: z.nullable(notEmptyString),
	Prompt_Text: notEmptyString,
	Tooltip: z.nullable(notEmptyString),
	ICD10: z.nullable(notEmptyString),
	ICD10_Description: z.nullable(notEmptyString),
	Range_Low: z.nullable(notEmptyString),
	Normal_Low: z.nullable(notEmptyString),
	Normal_High: z.nullable(notEmptyString),
	Range_High: z.nullable(notEmptyString),
	Required: z.boolean(),
})
export type DirectoryItem = z.infer<typeof ZDirectoryItem>

export const newDirectoryItem = (itemId: number, promptText: string): DirectoryItem => {
	return {
		ItemID: itemId,
		Class: null,
		Prompt_Text: promptText,
		Tooltip: null,
		ICD10: null,
		ICD10_Description: null,
		Range_Low: null,
		Normal_Low: null,
		Normal_High: null,
		Range_High: null,
		Required: false,
	}
}

export const dirItemIsDx = (item: DirectoryItem): boolean => (item.Class === 'DX')

export const ZValuelistItem = z.object({
	Name: notEmptyString,
	Item: notEmptyString,
})
export type ValuelistItem = z.infer<typeof ZValuelistItem>

type Cidable<T extends object> = T & {
	Cid: string
}

export class Directory {
	dirItemsById: Map<number, Cidable<DirectoryItem>>
	valuelistItemsByName: Map<string, Cidable<{Value: string}>[]>

	constructor(dirItems: DirectoryItem[], valuelistItems: ValuelistItem[]) {
		this.dirItemsById = new Map<number, Cidable<DirectoryItem>>()
		for(let item of dirItems) {
			this.dirItemsById.set(item.ItemID, {
				...item,
				Cid: cidForDirItemId(item.ItemID),
			})
		}
		this.valuelistItemsByName = new Map<string, Cidable<{Value: string}>[]>()
		for(let item of valuelistItems) {
			let namedList = this.valuelistItemsByName.get(item.Name)
			if(!namedList) {
				namedList = []
			}
			namedList.push({
				Value: item.Item,
				Cid: cidForDirValue(item.Item),
			})
			this.valuelistItemsByName.set(item.Name, namedList)
		}
	}

	getItem(itemId: number): Cidable<DirectoryItem>|null {
		return this.dirItemsById.get(itemId) ?? null
	}

	getValuelist(name: string): Cidable<{Value: string}>[]|null {
		return this.valuelistItemsByName.get(name) ?? null
	}
}
